/**
 * 受付端末 `/reception/admin/devices`(reception:admin。設計書 §4.3、計画 第 6 段)。
 * 一覧(状態・最終通信・電池・未送信の数・トークンの期限)、登録コードの発行(平文は 1 度だけ
 * 表示。10 分・1 回)、名前・カメラ・向きの変更、失効(理由は必須。未送信キューの回収は
 * 退役・交換のときだけ選べる)。端末のトークンそのものは画面にも API にも出ない。
 */
import { useMutation } from "@tanstack/react-query";
import { Alert, Button, Checkbox, Form, Input, Modal, Select, Space, Typography, message } from "antd";
import { useState } from "react";

import { DataTable, PageHeader, StatusTag, TimeAgo, type DataColumn } from "../../ui";
import {
  errorText,
  useDevices,
  useInvalidateReception,
  useLocations,
  useReceptionPerms,
  useSend,
  type EnrollCode,
  type ReceptionDevice,
} from "./hooks";

const { Text, Paragraph } = Typography;

const REVOKE_REASONS = [
  { value: "retired", label: "退役(使わなくなった)" },
  { value: "replaced", label: "交換(新しい端末に替えた)" },
  { value: "lost", label: "紛失" },
  { value: "compromised", label: "不正利用の疑い" },
];
const RECOVERABLE = ["retired", "replaced"];
const reasonLabel = (r: string | null) => REVOKE_REASONS.find((x) => x.value === r)?.label ?? r ?? "";

function deviceState(d: ReceptionDevice) {
  if (d.revoked_at) return <StatusTag tone="neutral">失効</StatusTag>;
  if (d.token_expires_at && new Date(d.token_expires_at).getTime() <= Date.now())
    return <StatusTag tone="danger">期限切れ</StatusTag>;
  if (!d.last_heartbeat_at) return <StatusTag tone="info">未接続</StatusTag>;
  return d.online ? <StatusTag tone="success">稼働中</StatusTag> : <StatusTag tone="warning">途絶</StatusTag>;
}

export function DevicesPage() {
  const { canAdmin, readOnly } = useReceptionPerms();
  const locations = useLocations();
  const locs = locations.data?.items ?? [];
  const [locationId, setLocationId] = useState<number | null>(null);
  const query = useDevices(locationId);
  const rows = query.data?.items ?? [];
  const [enrolling, setEnrolling] = useState<{ device: ReceptionDevice | null } | null>(null);
  const [editing, setEditing] = useState<ReceptionDevice | null>(null);
  const locName = (id: number) => locs.find((l) => l.id === id)?.name ?? `#${id}`;

  const columns: DataColumn<ReceptionDevice>[] = [
    { title: "端末", ellipsis: false, dataIndex: "name", width: 180 },
    { title: "拠点", ellipsis: false, dataIndex: "location_id", width: 160, render: (v: number) => locName(v) },
    { title: "状態", ellipsis: false, width: 110, render: (_: unknown, d) => deviceState(d) },
    {
      title: "最終通信",
      ellipsis: false,
      dataIndex: "last_heartbeat_at",
      width: 160,
      render: (v: string | null) => (v ? <TimeAgo iso={v} /> : "—"),
    },
    { title: "電池", ellipsis: false, dataIndex: "battery", width: 90, render: (v: number | null) => (v === null ? "—" : `${v}%`) },
    { title: "未送信", ellipsis: false, dataIndex: "queue_len", width: 160, render: (v: number | null) => <span className="ui-num">{v ?? 0}</span> },
    {
      title: "トークンの期限",
      ellipsis: false,
      dataIndex: "token_expires_at",
      width: 200,
      render: (v: string | null, d) => (d.revoked_at ? reasonLabel(d.revoke_reason) : v ? <TimeAgo iso={v} /> : "—"),
    },
    { title: "版", ellipsis: false, dataIndex: "app_version", width: 100, render: (v: string | null) => v || "—" },
  ];

  return (
    <div className="ui-list">
      <div className="ui-list-main">
        <PageHeader
          title="受付端末"
          description="拠点の受付に置くタブレット。登録コード(10 分・1 回限り)を端末の登録画面に入れると、その拠点の端末になる。紛失したら失効させる(新旧のトークンとも即時に無効)。"
          primary={
            canAdmin ? (
              <Button type="primary" disabled={readOnly || locs.length === 0} onClick={() => setEnrolling({ device: null })}>
                端末を登録
              </Button>
            ) : undefined
          }
        />
        <Space style={{ marginBottom: 12 }}>
          <Select<number | null>
            style={{ width: 240 }}
            value={locationId}
            onChange={setLocationId}
            options={[{ value: null, label: "すべての拠点" }, ...locs.map((l) => ({ value: l.id, label: l.name }))]}
          />
        </Space>
        {query.isError && <Alert type="error" showIcon message="端末を取得できなかった" description={errorText(query.error)} />}
        <DataTable<ReceptionDevice>
          columns={columns}
          scrollX={columns.reduce((sum, c) => sum + c.width, 0)}
          rows={rows}
          rowKey={(r) => r.id}
          loading={query.isFetching}
          total={rows.length}
          pageSize={Math.max(rows.length, 1)}
          onPage={() => undefined}
          onRowClick={(row) => setEditing(row)}
          rowTone={(row) => (row.revoked_at ? "neutral" : undefined)}
          error={query.isError ? "取得できなかったので表示できない" : undefined}
          empty={{ text: "端末がない。「端末を登録」から登録コードを出す" }}
        />
      </div>
      {enrolling && (
        <EnrollModal
          device={enrolling.device}
          defaultLocation={locationId ?? locs.find((l) => l.enabled)?.id ?? null}
          locations={locs.filter((l) => l.enabled).map((l) => ({ value: l.id, label: l.name }))}
          onClose={() => setEnrolling(null)}
        />
      )}
      {editing && (
        <DeviceModal
          device={editing}
          locationName={locName(editing.location_id)}
          onClose={() => setEditing(null)}
          onReenroll={() => {
            setEnrolling({ device: editing });
            setEditing(null);
          }}
        />
      )}
    </div>
  );
}

function EnrollModal({
  device,
  defaultLocation,
  locations,
  onClose,
}: {
  device: ReceptionDevice | null;
  defaultLocation: number | null;
  locations: { value: number; label: string }[];
  onClose: () => void;
}) {
  const send = useSend();
  const invalidate = useInvalidateReception();
  const [issued, setIssued] = useState<EnrollCode | null>(null);
  const issue = useMutation({
    mutationFn: (v: { location_id: number; device_name?: string }) =>
      send<EnrollCode>("POST", "/enroll-codes", {
        location_id: device ? device.location_id : v.location_id,
        device_name: (v.device_name ?? "").trim(),
        ...(device ? { device_id: device.id } : {}),
      }),
    onSuccess: (r) => {
      setIssued(r);
      invalidate();
    },
    onError: (e) => void message.error(errorText(e)),
  });

  return (
    <Modal open title={device ? `再登録: ${device.name}` : "端末を登録"} onCancel={onClose} footer={null} destroyOnClose>
      {issued ? (
        <>
          <Paragraph>端末の登録画面にこのコードを入れる。この画面を閉じると二度と表示されない。</Paragraph>
          <div aria-label="登録コード" style={{ fontSize: 36, fontFamily: "monospace", letterSpacing: "0.2em", margin: "16px 0" }}>
            {issued.code.slice(0, 4)} {issued.code.slice(4)}
          </div>
          <Paragraph type="secondary">
            期限: <TimeAgo iso={issued.expires_at} />(10 分・1 回限り)
          </Paragraph>
          <Button type="primary" onClick={onClose}>
            閉じる
          </Button>
        </>
      ) : (
        <Form<{ location_id: number; device_name?: string }>
          layout="vertical"
          initialValues={{ location_id: device?.location_id ?? defaultLocation ?? undefined, device_name: device?.name ?? "" }}
          disabled={issue.isPending}
          onFinish={(v) => issue.mutate(v)}
        >
          {device ? (
            <Paragraph>
              同じ端末(ID・履歴はそのまま)に新しいトークンを出す。工場出荷に戻した・Cookie が消えたときに使う。
            </Paragraph>
          ) : (
            <Form.Item name="location_id" label="拠点" rules={[{ required: true, message: "拠点を選ぶ" }]}>
              <Select options={locations} />
            </Form.Item>
          )}
          <Form.Item
            name="device_name"
            label="端末の名前"
            rules={device ? [] : [{ required: true, whitespace: true, message: "名前を入れる(例: 1F 受付 左)" }]}
          >
            <Input maxLength={200} placeholder="1F 受付 左" />
          </Form.Item>
          <Space>
            <Button onClick={onClose}>やめる</Button>
            <Button type="primary" htmlType="submit" loading={issue.isPending}>
              登録コードを出す
            </Button>
          </Space>
        </Form>
      )}
    </Modal>
  );
}

type DeviceForm = { name: string; camera_side: "front" | "back"; orientation: "landscape" | "portrait" };
type RevokeForm = { reason?: string; recover_queue?: boolean };

function DeviceModal({
  device,
  locationName,
  onClose,
  onReenroll,
}: {
  device: ReceptionDevice;
  locationName: string;
  onClose: () => void;
  onReenroll: () => void;
}) {
  const send = useSend();
  const invalidate = useInvalidateReception();
  const { readOnly } = useReceptionPerms();
  const [revokeForm] = Form.useForm<RevokeForm>();
  const reason = Form.useWatch("reason", revokeForm);
  const revoked = device.revoked_at !== null;

  const save = useMutation({
    mutationFn: (v: DeviceForm) => send<ReceptionDevice>("PATCH", `/devices/${device.id}`, { ...v, name: v.name.trim() }),
    onSuccess: () => {
      void message.success("保存した(監査ログに記録した)");
      invalidate();
      onClose();
    },
    onError: (e) => void message.error(errorText(e)),
  });
  const revoke = useMutation({
    mutationFn: (v: RevokeForm) =>
      send<ReceptionDevice>("POST", `/devices/${device.id}/revoke`, {
        reason: v.reason,
        recover_queue: RECOVERABLE.includes(v.reason ?? "") && !!v.recover_queue,
      }),
    onSuccess: () => {
      void message.success("失効させた(監査ログに記録した)");
      invalidate();
      onClose();
    },
    onError: (e) => void message.error(errorText(e)),
  });

  return (
    <Modal open width={640} title={`端末: ${device.name}`} onCancel={onClose} footer={null} destroyOnClose>
      <Paragraph type="secondary">
        拠点: {locationName} / 種類: {device.platform || "—"} / 登録: {device.created_at ? <TimeAgo iso={device.created_at} /> : "—"}
      </Paragraph>
      {revoked ? (
        <Alert
          type="info"
          showIcon
          message={`失効済み(${reasonLabel(device.revoke_reason)})`}
          description={
            device.queue_recovery_until ? (
              <>
                未送信キューの回収を受け付ける期限: <TimeAgo iso={device.queue_recovery_until} />
              </>
            ) : undefined
          }
        />
      ) : (
        <>
          <Form<DeviceForm>
            layout="vertical"
            initialValues={{ name: device.name, camera_side: device.camera_side, orientation: device.orientation }}
            disabled={readOnly || save.isPending}
            onFinish={(v) => save.mutate(v)}
          >
            <Form.Item name="name" label="端末の名前" rules={[{ required: true, whitespace: true, message: "名前を入れる" }]}>
              <Input maxLength={200} />
            </Form.Item>
            <Space size={16} wrap>
              <Form.Item name="camera_side" label="QR を読むカメラ">
                <Select
                  style={{ width: 200 }}
                  options={[
                    { value: "front", label: "前面(画面側)" },
                    { value: "back", label: "背面" },
                  ]}
                />
              </Form.Item>
              <Form.Item name="orientation" label="画面の向き">
                <Select
                  style={{ width: 160 }}
                  options={[
                    { value: "landscape", label: "横" },
                    { value: "portrait", label: "縦" },
                  ]}
                />
              </Form.Item>
            </Space>
            <Space>
              <Button type="primary" htmlType="submit" loading={save.isPending}>
                保存
              </Button>
              <Button disabled={readOnly} onClick={onReenroll}>
                再登録のコードを出す
              </Button>
            </Space>
          </Form>
          <Typography.Title level={5} style={{ marginTop: 24 }}>
            失効
          </Typography.Title>
          <Text type="secondary">新旧のトークンとも即時に無効になり、端末は登録画面に戻る。取り消せない。</Text>
          <Form<RevokeForm>
            form={revokeForm}
            layout="vertical"
            style={{ marginTop: 12 }}
            disabled={readOnly || revoke.isPending}
            onFinish={(v) =>
              Modal.confirm({
                title: `「${device.name}」を失効させますか?`,
                content: "この端末では受付できなくなります。",
                okText: "失効させる",
                okButtonProps: { danger: true },
                cancelText: "戻る",
                onOk: () => revoke.mutateAsync(v),
              })
            }
          >
            <Form.Item name="reason" label="理由" rules={[{ required: true, message: "理由を選ぶ" }]}>
              <Select style={{ width: 280 }} options={REVOKE_REASONS} />
            </Form.Item>
            {RECOVERABLE.includes(reason ?? "") && (
              <Form.Item name="recover_queue" valuePropName="checked" extra="24 時間、端末に残った未送信の受付を受け取る(紛失・不正利用の疑いでは選べない)">
                <Checkbox>未送信キューを回収する</Checkbox>
              </Form.Item>
            )}
            <Button danger htmlType="submit" loading={revoke.isPending}>
              失効させる
            </Button>
          </Form>
        </>
      )}
    </Modal>
  );
}
